// Console: فقط کاربران دارای نقش پلتفرم (Realm Role platform-*)؛ بدون انتخاب مستأجر (AP-008، PRD-PLT §2).
import Button from '@mui/material/Button';
import { createPhoenixClient, unwrap, type MeResponse, type PhoenixClient } from '@phoenix/api-client';
import { acceptLanguage, useTranslation } from '@phoenix/i18n';
import { EmptyState, ErrorState, LoadingState } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';
import { useAuth } from './AuthProvider';

export interface PlatformSessionValue {
  me: MeResponse;
  api: PhoenixClient;
  hasPlatformRole: (roles: readonly string[]) => boolean;
}

const PlatformSessionContext = createContext<PlatformSessionValue | null>(null);

export interface PlatformGateProps {
  children: ReactNode;
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
}

export function PlatformGate({ children, baseUrl = '', fetch: customFetch }: PlatformGateProps) {
  const auth = useAuth();
  const { t, i18n } = useTranslation('auth');
  const authRef = useRef(auth);
  authRef.current = auth;
  const languageRef = useRef(i18n.language);
  languageRef.current = i18n.language;

  const api = useMemo(
    () =>
      createPhoenixClient({
        baseUrl,
        fetch: customFetch,
        getLanguage: () => acceptLanguage(languageRef.current),
        getToken: (options) => authRef.current.getAccessToken(options),
        onUnauthorized: async () => true,
      }),
    [baseUrl, customFetch],
  );

  const query = useQuery({
    queryKey: ['identity', 'me'],
    queryFn: () => unwrap(api.GET('/api/v1/identity/me')),
  });

  const value = useMemo<PlatformSessionValue | null>(() => {
    if (!query.data) {
      return null;
    }
    const roles = new Set(query.data.platformRoles);
    return { me: query.data, api, hasPlatformRole: (required) => required.some((role) => roles.has(role)) };
  }, [query.data, api]);

  if (query.isPending) {
    return <LoadingState />;
  }
  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (!value || !value.me.isPlatformUser) {
    return (
      <div data-screen-id="CONSOLE-NOT-PLATFORM-USER">
        <EmptyState
          titleKey="auth:console.not_platform_user_title"
          descriptionKey="auth:console.not_platform_user_description"
          action={
            <Button variant="outlined" onClick={() => void auth.logout()} data-testid="logout">
              {t('auth:account.logout')}
            </Button>
          }
        />
      </div>
    );
  }
  return <PlatformSessionContext.Provider value={value}>{children}</PlatformSessionContext.Provider>;
}

export function usePlatformSession(): PlatformSessionValue {
  const context = useContext(PlatformSessionContext);
  if (!context) {
    throw new Error('usePlatformSession must be used inside <PlatformGate>.');
  }
  return context;
}
